/**
 * Dev-only stand-ins for a tracked friend, so the map's close-range behaviour can be looked at without two phones
 * on a field (Sam, 2026-10-04: "give me screenshots so I can give you feedback"). `?scenario=<name>` on a map
 * URL in a dev build replaces the live session with one friend in a known state, you at the venue's centre.
 *
 * Names: uwb-direction | uwb-distance | ble-gps | gps | arrive-arrow | arrive-near | stale
 * crowd: three friends at once - far on GPS, close with UWB, and one last seen 35 min ago (the Maps + Find options).
 * tutorial: the intro's Find shot (2026-10-23) - made-up names, north up: Maya tracked 16 m straight ahead, Leo and Ava
 *   together off to the right.
 */
import { destinationPoint, haversineMeters, bearingDegrees } from '../proximity/engine/geodesy';
import { formatFindDistance } from '../proximity/engine/findDistanceFormat';
import { formatStalenessShort } from '../proximity/engine/staleness';

export const MAP_SCENARIOS = [
  'uwb-direction',
  'uwb-distance',
  'ble-gps',
  'gps',
  'arrive-arrow',
  'arrive-near',
  'stale',
  'crowd',
  'tutorial',
  'welcome',
];

export function scenarioNameFromSearch(search) {
  // The welcome flow's demo (lib/welcomeDemo) is the one scenario a release build shows.
  if (typeof search === 'string' && new URLSearchParams(search).get('demo') === 'welcome')
    return 'welcome';
  if (!import.meta.env.DEV || typeof search !== 'string') return null;
  const name = new URLSearchParams(search).get('scenario');
  return name && MAP_SCENARIOS.includes(name) ? name : null;
}

/** Dev only: `&name=` renames the stand-in friend (a long name shows what the person row does with it). */
export function scenarioFriendNameFromSearch(search) {
  if (!import.meta.env.DEV || typeof search !== 'string') return null;
  const n = new URLSearchParams(search).get('name');
  return n && n.trim() ? n.trim().slice(0, 40) : null;
}

const FRIEND = { peerId: 'scenario-friend', displayName: 'Olivia', photoUrl: null };

/** @param bounds [[minLng,minLat],[maxLng,maxLat]] */
export function buildMapScenario(name, bounds, now = Date.now(), friendName = null) {
  if (!name || !bounds) return null;
  const centre = { lat: (bounds[0][1] + bounds[1][1]) / 2, lng: (bounds[0][0] + bounds[1][0]) / 2 };
  const self = {
    lat: centre.lat,
    lng: centre.lng,
    accuracyMeters: name === 'crowd' ? 14 : 5,
    headingDegrees: null,
    timestampMs: now,
  };
  const headingDeg = 20; // the phone faces a little east of north
  const at = (m, bearing) => destinationPoint(self.lat, self.lng, m, bearing);

  // The welcome flow (Stage 10, Sam 2026-10-26): Portola 2026, you in the open middle between Pier Stage and Ship Tent,
  // Maya close by, Leo a minute's walk off, Ava and Jordan together in the open short of Ship Tent. Fixed points on the
  // grounds (read off the published map), not the bounds' centre - that is the bay at Pier 80. Nobody is tracked.
  if (name === 'welcome') {
    const me = { ...self, lat: 37.74962, lng: -122.38005, accuracyMeters: 5 };
    const toward = (lat, lng) => ({
      m: haversineMeters(me.lat, me.lng, lat, lng),
      b: bearingDegrees(me.lat, me.lng, lat, lng),
    });
    const from = (m, b) => destinationPoint(me.lat, me.lng, m, b);
    const friends = [
      { peerId: 'welcome-maya', displayName: 'Maya', P: toward(37.74978, -122.37985), age: 4_000 },
      { peerId: 'welcome-leo', displayName: 'Leo', P: toward(37.74934, -122.37947), age: 9_000 },
      { peerId: 'welcome-ava', displayName: 'Ava', P: toward(37.74993, -122.37822), age: 6_000 },
      {
        peerId: 'welcome-jordan',
        displayName: 'Jordan',
        P: toward(37.74968, -122.37812),
        age: 11_000,
      },
    ];
    const built = friends.map((f) =>
      scenarioFriend(
        f,
        {
          uwb: null,
          gps: { m: f.P.m, b: f.P.b, age: f.age, src: 'remote', acc: 6 },
          kind: 'gps-live',
        },
        from,
        now,
      ),
    );
    // Opens over all five of you, not on you alone.
    const openAt = { lat: 37.7497, lng: -122.379, radius: 330 };
    return {
      name,
      self: me,
      openAt,
      headingDeg: 0,
      peers: built.map((b) => b.row),
      gpsPeers: built.map((b) => b.gpsPeer).filter(Boolean),
      trackedPeerId: null,
      selectPeerId: null,
      guide: false,
    };
  }
  if (name === 'tutorial') {
    const friends = [
      {
        peerId: 'scenario-maya',
        displayName: 'Maya',
        S: { uwb: null, gps: { m: 16, b: 0, age: 3_000, src: 'remote', acc: 5 }, kind: 'gps-live' },
      },
      {
        peerId: 'scenario-leo',
        displayName: 'Leo',
        S: {
          uwb: null,
          gps: { m: 19, b: 66, age: 9_000, src: 'remote', acc: 8 },
          kind: 'gps-live',
        },
      },
      {
        peerId: 'scenario-ava',
        displayName: 'Ava',
        S: {
          uwb: null,
          gps: { m: 21, b: 74, age: 14_000, src: 'remote', acc: 8 },
          kind: 'gps-live',
        },
      },
    ];
    const built = friends.map((f) => scenarioFriend(f, f.S, at, now));
    return {
      name,
      self,
      headingDeg: 0,
      peers: built.map((b) => b.row),
      gpsPeers: built.map((b) => b.gpsPeer).filter(Boolean),
      trackedPeerId: 'scenario-maya',
      selectPeerId: null,
      guide: false,
    };
  }
  if (name === 'crowd') {
    const friends = [
      {
        peerId: 'scenario-olivia',
        displayName: 'Olivia',
        S: {
          uwb: null,
          gps: { m: 140, b: 330, age: 12_000, src: 'remote', acc: 32 },
          kind: 'gps-live',
        },
      },
      {
        peerId: 'scenario-jo',
        displayName: 'Jo',
        S: {
          uwb: { d: 16, b: 35, dir: true },
          gps: { m: 21, b: 50, age: 6_000, src: 'remote', acc: 6 },
          kind: 'uwb-live',
        },
      },
      {
        peerId: 'scenario-maya',
        displayName: 'Maya',
        S: {
          uwb: null,
          gps: { m: 300, b: 200, age: 35 * 60_000, src: 'remote', acc: 55 },
          kind: 'gps-stale',
        },
      },
    ];
    const built = friends.map((f) => scenarioFriend(f, f.S, at, now));
    return {
      name,
      self,
      headingDeg,
      peers: built.map((b) => b.row),
      gpsPeers: built.map((b) => b.gpsPeer).filter(Boolean),
      trackedPeerId: 'scenario-jo',
      selectPeerId: null,
      guide: false,
    };
  }

  const S = {
    // Friend 6 m away at 70°, UWB with direction. GPS (25 m, 130°) is wrong on purpose: the puck must follow UWB.
    'uwb-direction': {
      uwb: { d: 6, b: 70, dir: true },
      gps: { m: 25, b: 130, age: 8_000, src: 'remote' },
      kind: 'uwb-live',
      guide: false,
    },
    // UWB range 8 m, no direction: puck at GPS (20 m, 300°), ring at 8 m.
    'uwb-distance': {
      uwb: { d: 8, b: null, dir: false },
      gps: { m: 20, b: 300, age: 5_000, src: 'remote' },
      kind: 'uwb-live',
      guide: false,
    },
    // No service for either phone: the friend's GPS arrived over Bluetooth, 35 m at 220°, 3 s old.
    'ble-gps': {
      uwb: null,
      gps: { m: 35, b: 220, age: 3_000, src: 'ble-gps' },
      kind: 'gps-live',
      guide: false,
    },
    // Plain server GPS, 60 m at 10°.
    gps: {
      uwb: null,
      gps: { m: 60, b: 10, age: 20_000, src: 'remote' },
      kind: 'gps-live',
      guide: false,
    },
    // Guiding to a friend 18 m away by GPS: the arrow is on.
    'arrive-arrow': {
      uwb: null,
      gps: { m: 18, b: 40, age: 4_000, src: 'remote' },
      kind: 'gps-live',
      guide: true,
    },
    // Guiding, UWB 1.5 m with direction: inside the arrival radius, arrow retired.
    'arrive-near': {
      uwb: { d: 1.5, b: 95, dir: true },
      gps: { m: 9, b: 60, age: 6_000, src: 'remote' },
      kind: 'uwb-live',
      guide: true,
    },
    // Seen 40 minutes ago, 90 m away.
    stale: {
      uwb: null,
      gps: { m: 90, b: 250, age: 40 * 60_000, src: 'remote' },
      kind: 'gps-stale',
      guide: false,
    },
  }[name];
  if (!S) return null;

  const { row, gpsPeer } = scenarioFriend(
    { ...FRIEND, displayName: friendName || FRIEND.displayName },
    S,
    at,
    now,
  );
  return {
    name,
    self,
    headingDeg,
    peers: [row],
    gpsPeers: gpsPeer ? [gpsPeer] : [],
    trackedPeerId: FRIEND.peerId,
    selectPeerId: FRIEND.peerId,
    guide: S.guide,
  };
}

function scenarioFriend(friend, S, at, now) {
  const gpsPoint = S.gps ? at(S.gps.m, S.gps.b) : null;
  const gpsPeer = gpsPoint
    ? {
        id: friend.peerId,
        source: S.gps.src,
        lat: gpsPoint.lat,
        lng: gpsPoint.lng,
        accuracyMeters: S.gps.acc ?? 8,
        headingDegrees: null,
        lastKnownAt: now - S.gps.age,
      }
    : null;
  const peer = {
    peerId: friend.peerId,
    rssi: -70,
    smoothedRssi: -70,
    estimatedDistance: S.uwb ? S.uwb.d : 12,
    zone: 'near',
    lastSeenAt: now - 300,
    gpsDistance: S.gps?.m,
    gpsBearingDeg: S.gps?.b,
    gpsSource: S.gps?.src,
    uwbActive: Boolean(S.uwb),
    uwbDistance: S.uwb?.d,
    uwbBearing: S.uwb?.dir ? S.uwb.b : undefined,
    uwbDirectionAvailable: Boolean(S.uwb?.dir),
  };
  const row = {
    peerId: friend.peerId,
    displayName: friend.displayName,
    photoUrl: friend.photoUrl ?? null,
    display: {
      kind: S.kind,
      listPreview: scenarioPreview(S, gpsPeer),
      distanceMeters: S.uwb ? S.uwb.d : (S.gps?.m ?? null),
      sortTier: S.uwb ? 0 : 1,
      sortDistance: S.uwb ? S.uwb.d : (S.gps?.m ?? 0),
      gpsLastKnownAt: gpsPeer?.lastKnownAt ?? null,
    },
    peer,
    bearingDeg: S.uwb?.dir ? S.uwb.b : (S.gps?.b ?? null),
    uwbActive: Boolean(S.uwb),
    uwbDistance: S.uwb?.d ?? null,
    uwbDirectionAvailable: Boolean(S.uwb?.dir),
  };
  return { row, gpsPeer };
}

/** The Find list's one line under a name, as the engine would write it: distance, then how old the position is. */
function scenarioPreview(S, gpsPeer) {
  const m = S.uwb ? S.uwb.d : S.gps?.m;
  if (m == null) return null;
  const age = S.uwb ? 'now' : gpsPeer ? formatStalenessShort(gpsPeer.lastKnownAt) : null;
  return [formatFindDistance(m), age].filter(Boolean).join(' · ');
}
